/* eslint-disable no-restricted-syntax */

import { parse, stringify } from 'query-string';

import { getCurrentInstitution } from './routeThunks/institution/getCurrentInstitution';
import { selectCurrentOrigin } from './location/selectors';

/* global _OL_ORIGIN */

export const CUSTOM_DOMAINS = 'CUSTOM_DOMAINS';
export const MAIN_DOMAIN = 'MAIN_DOMAIN';

export const MAIN_DOMAIN_OPTIONS = {
  domain: MAIN_DOMAIN,
};

export const STAY_ON_ANY_DOMAIN = {
  stickyDomain: true,
  domains: [
    {
      domain: CUSTOM_DOMAINS,
      makeSelectOriginForAction: () => selectCurrentOrigin,
      duringThunk: getCurrentInstitution,
    },
    MAIN_DOMAIN_OPTIONS,
  ],
};

export const querySerializer = {
  stringifyQuery: (query) => stringify(query, { arrayFormat: 'bracket' }),
  parseSearch: (search) => parse(search, { arrayFormat: 'bracket' }),
};

export const isOnDomainType = (origin, domainType) => {
  if (origin === _OL_ORIGIN) {
    return domainType === MAIN_DOMAIN;
  } else {
    return domainType === CUSTOM_DOMAINS;
  }
};

const defaultThunk = () => Promise.resolve();

const defaultDomains = [MAIN_DOMAIN_OPTIONS];

// We never rely on case in urls, and urls are always strings
export const fromPath = (value) => {
  if (Array.isArray(value)) {
    return value.map((segment) => segment.toLowerCase()).join('/');
  }
  if (value !== undefined) {
    return value.toLowerCase();
  }
  return value;
};

export const toPath = (value, { repeat }) => {
  if (repeat) {
    return value
      ? value.split('/').map((segment) => segment.toLowerCase())
      : [];
  }
  if (value === null) {
    return undefined;
  }
  if (value !== undefined) {
    return value.toLowerCase();
  }
  return value;
};

export const makeRouteForOrigin = (multiDomainRoute, origin) => {
  const { domains, stickyDomain: _, ...baseRoute } = multiDomainRoute;

  baseRoute.fromPath = fromPath;

  baseRoute.toPath = toPath;

  // If domains isn't specified, the default is [MAIN_DOMAIN_OPTIONS]
  for (const domainOptions of domains || defaultDomains) {
    const { domain, ...domainRoute } = domainOptions;
    if (isOnDomainType(origin, domain)) {
      // We match the first domain option that we are on
      const baseThunk = baseRoute.thunk || defaultThunk;
      let thunk = baseThunk;
      if (domainRoute.duringThunk) {
        thunk = (request) =>
          Promise.all([baseThunk(request), domainRoute.duringThunk(request)]);
      }
      return {
        ...baseRoute,
        ...domainRoute,
        thunk,
      };
    }
  }

  // None of the domains matched, so this route is here to link to other domains
  return {
    ...baseRoute,
    path: null, // We are on the wrong domain, don't match this!
    /**
     * If this action is dispatched, the page will reload anyway!
     * Exception: pathless routes on custom domains
     */
    thunk: baseRoute.path ? null : baseRoute.thunk,
  };
};

export const makeRoutesMapForOrigin = (multiDomainRoutesMap, origin) => {
  // given an origin, take multiDomainRoutesMap and return
  // a normal routesMap for this origin. Routes that are not available on this
  // origin will still be in the returned routesMap, but they will be pathless
  const result = {};
  Object.keys(multiDomainRoutesMap).forEach((routeType) => {
    const multiDomainRoute = multiDomainRoutesMap[routeType];
    result[routeType] = makeRouteForOrigin(multiDomainRoute, origin);
  });
  return result;
};
